import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { Badge, CloseButton, Flex, Heading, Input, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import Editor from "@monaco-editor/react";
import type { SeriesOptionsType } from "highcharts";
import { useData } from "vike-react/useData";
import { AccessibleFormControl } from "@/components/AccessibleFormControl/AccessibleFormControl";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { ChakraThemeProvider } from "@/components/Providers/ChakraThemeProvider";
import Button from "@/components/chakra/Button";
import { GET } from "@/services/api/client";
import { getAllEvents } from "@/services/cachingSystem/cacheDependencyMap";
import type { DependencyStackItem } from "@/services/cachingSystem/resolveCacheDependenciesArr";
import { resolveCacheDependenciesArr } from "@/services/cachingSystem/resolveCacheDependenciesArr";
import { resolveCacheDependenciesStack } from "@/services/cachingSystem/resolveCacheDependenciesStack";
import type { CacheStatsData } from "@/types/CacheStatsData";
import type { PageData } from "@/types/PageData";

const statToChartSeriesData = (data: Record<string, number>, statName: string, type: "column" | "pie"): SeriesOptionsType => {
	return {
		name: statName,
		type,
		data: [
			{
				name: "Hit",
				y: data[`${statName}:hit`] || 0
			},
			{
				name: "Miss",
				y: data[`${statName}:miss`] || 0
			}
		]
	};
};

interface EditorTabBase {
	label: string;
	type: string;
	content: any;
	isLoading?: boolean;
}

interface ErrorTab extends EditorTabBase {
	type: "error";
}

interface GetTab extends EditorTabBase {
	type: "get";
	content: object;
}

interface DeleteTab extends EditorTabBase {
	type: "delete";
	content: object;
}

interface RevalidateTab extends EditorTabBase {
	type: "revalidate";
	content: object;
}

interface ScanTab extends EditorTabBase {
	type: "scan";
	content: string[];
}

interface EventTreeTab extends EditorTabBase {
	type: "eventTree";
	content: { stack: DependencyStackItem; arr: string[] };
}

type EditorTab = DeleteTab | GetTab | ScanTab | ErrorTab | RevalidateTab | EventTreeTab;

export default function CacheStatsPageEntryPoint() {
	const [config, setConfig] = useState<CacheStatsData["cacheConfig"]>({});
	const [tabs, setTabs] = useState<EditorTab[]>([]);
	const [currentTab, setCurrentTab] = useState<number>(0);
	const [pattern, setPattern] = useState<string>("");
	const [getKey, setGetKey] = useState<string>("");
	const [eventName, setEventName] = useState<string>("");
	const [deleteKey, setDeleteKey] = useState<string>("");
	const [revalidateKey, setRevalidateKey] = useState<string>("");
	const dataResponse = useData<PageData<CacheStatsData>>();

	const refreshData = useCallback(async () => {
		const response = await GET<any>("/api/v1/cache/config");
		if (response.isErr) {
			return;
		}

		setConfig(response.body.cacheConfig);
	}, []);

	useEffect(() => {
		refreshData();
	}, [refreshData]);

	const updateConfig = useCallback(
		(name: string, value: string) => {
			return async () => {
				await GET(`/api/v1/cache/config/${name}/${value}`);
				refreshData();
			};
		},
		[refreshData]
	);

	const scan = useCallback(
		async (pattern: string) => {
			// Create a new tab with loading state
			const newTabIndex = tabs.length;
			const newTabs: EditorTab[] = [...tabs];
			newTabs.push({ label: `Scan: ${pattern}`, type: "scan", content: [], isLoading: true });
			setTabs(newTabs);
			setCurrentTab(newTabIndex);

			const scanResponse = await GET(`/api/v1/cache/scan/${pattern}`);

			// Update the tab with the response data
			setTabs((prevTabs) => {
				const updatedTabs = [...prevTabs];
				if (scanResponse.isErr) {
					updatedTabs[newTabIndex] = {
						label: `Scan: ${pattern}`,
						type: "error",
						content: scanResponse.err,
						isLoading: false
					};
				} else {
					updatedTabs[newTabIndex] = {
						label: `Scan: ${pattern}`,
						type: "scan",
						content: scanResponse.body as string[],
						isLoading: false
					};
				}
				return updatedTabs;
			});

			setPattern("");
		},
		[tabs]
	);

	const scanWithState = useCallback(async () => {
		scan(pattern);
	}, [scan, pattern]);

	const get = useCallback(
		async (getKey: string) => {
			// Create a new tab with loading state
			const newTabIndex = tabs.length;
			const newTabs: EditorTab[] = [...tabs];
			newTabs.push({ label: `Get: ${getKey}`, type: "get", content: {}, isLoading: true });
			setTabs(newTabs);
			setCurrentTab(newTabIndex);

			const getResponse = await GET(`/api/v1/cache/get/${getKey}`);

			// Update the tab with the response data
			setTabs((prevTabs) => {
				const updatedTabs = [...prevTabs];
				if (getResponse.isErr) {
					updatedTabs[newTabIndex] = {
						label: `Get: ${getKey}`,
						type: "error",
						content: getResponse.err,
						isLoading: false
					};
				} else {
					updatedTabs[newTabIndex] = {
						label: `Get: ${getKey}`,
						type: "get",
						content: getResponse.body as object,
						isLoading: false
					};
				}
				return updatedTabs;
			});

			setPattern("");
		},
		[tabs]
	);

	const deleteCache = useCallback(
		async (deleteKey: string) => {
			// Create a new tab with loading state
			const newTabIndex = tabs.length;
			const newTabs: EditorTab[] = [...tabs];
			newTabs.push({ label: `Delete: ${deleteKey}`, type: "delete", content: {}, isLoading: true });
			setTabs(newTabs);
			setCurrentTab(newTabIndex);

			const deleteResponse = await GET(`/api/v1/cache/delete/${deleteKey}`);

			// Update the tab with the response data
			setTabs((prevTabs) => {
				const updatedTabs = [...prevTabs];
				if (deleteResponse.isErr) {
					updatedTabs[newTabIndex] = {
						label: `Delete: ${deleteKey}`,
						type: "error",
						content: deleteResponse.err,
						isLoading: false
					};
				} else {
					updatedTabs[newTabIndex] = {
						label: `Delete: ${deleteKey}`,
						type: "delete",
						content: deleteResponse.body as object,
						isLoading: false
					};
				}
				return updatedTabs;
			});
		},
		[tabs]
	);

	const revalidate = useCallback(
		async (revalidateKey: string) => {
			// Extract the part before "()" in the key
			const functionName = revalidateKey.split("(")[0];

			// Create a new tab with loading state
			const newTabIndex = tabs.length;
			const newTabs: EditorTab[] = [...tabs];
			newTabs.push({ label: `Revalidate: ${functionName}`, type: "revalidate", content: {}, isLoading: true });
			setTabs(newTabs);
			setCurrentTab(newTabIndex);

			const revalidateResponse = await GET(`/api/v1/cache/revalidate/${functionName}`);

			// Update the tab with the response data
			setTabs((prevTabs) => {
				const updatedTabs = [...prevTabs];
				if (revalidateResponse.isErr) {
					updatedTabs[newTabIndex] = {
						label: `Revalidate: ${functionName}`,
						type: "error",
						content: revalidateResponse.err,
						isLoading: false
					};
				} else {
					updatedTabs[newTabIndex] = {
						label: `Revalidate: ${functionName}`,
						type: "revalidate",
						content: revalidateResponse.body as object,
						isLoading: false
					};
				}
				return updatedTabs;
			});
		},
		[tabs]
	);

	const eventTree = useCallback(
		async (eventName: string) => {
			const newTabIndex = tabs.length;
			const newTabs: EditorTab[] = [...tabs];
			const stack = { name: eventName, deps: resolveCacheDependenciesStack(eventName) };
			const arr = resolveCacheDependenciesArr(stack);

			newTabs.push({ label: `Event Tree: ${eventName}`, type: "eventTree", content: { arr, stack }, isLoading: false });
			setTabs(newTabs);
			setCurrentTab(newTabIndex);
		},
		[tabs]
	);

	const getWithState = useCallback(async () => {
		get(getKey);
	}, [get, getKey]);

	const revalidateWithState = useCallback(async () => {
		revalidate(revalidateKey);
	}, [revalidate, revalidateKey]);

	const eventTreeWithState = useCallback(async () => {
		eventTree(eventName);
	}, [eventTree, eventName]);

	const deleteWithState = useCallback(async () => {
		deleteCache(deleteKey);
	}, [deleteCache, deleteKey]);

	const getHandler = useCallback(
		(key: string) => {
			return () => {
				get(key);
			};
		},
		[get]
	);

	const deleteHandler = useCallback((key: string) => {
		return () => {
			setDeleteKey(key);
		};
	}, []);

	const revalidateHandler = useCallback((key: string) => {
		return () => {
			const functionName = key.split("(")[0];
			setRevalidateKey(functionName);
		};
	}, []);

	const scanHandler = useCallback(
		(pattern: string) => {
			return async () => {
				scan(pattern);
			};
		},
		[scan]
	);

	const deleteTabHandler = useCallback((tabIndex: number) => {
		return (evt: MouseEvent<HTMLElement>) => {
			setTabs((currentTabs: EditorTab[]) => {
				return currentTabs.filter((tab, index) => {
					return index !== tabIndex;
				});
			});
			evt.preventDefault();
			evt.stopPropagation();
			return false;
		};
	}, []);

	if ("err" in dataResponse) {
		return (
			<Flex>
				<Text>Error in response from API:</Text>
				<code>{JSON.stringify(dataResponse.err)}</code>
			</Flex>
		);
	}

	const cacheStats = dataResponse.body.cacheStats;

	return (
		<ChakraThemeProvider>
			<Flex flexDirection="column" flex={1} gap={4}>
				<Flex flexDirection="row" gap={4}>
					<Flex flexDirection="column" flex={1} gap={4}>
						<Flex flexDirection="column" gap={4}>
							<Heading fontSize={18}>Scan</Heading>
							<AccessibleFormControl id="pattern">
								<Input
									size="sm"
									id="pattern"
									type="text"
									placeholder="e.g. getShareClassTheme*"
									value={pattern}
									onChange={(evt: ChangeEvent<HTMLInputElement>) => {
										setPattern(evt.target.value);
									}}
								/>
							</AccessibleFormControl>
							<Flex gap={2}>
								<Button size="sm" onClick={scanWithState} isDisabled={!pattern}>
									Scan...
								</Button>
							</Flex>
						</Flex>
						<Flex flexDirection="column" gap={4}>
							<Heading fontSize={18}>Get</Heading>
							<AccessibleFormControl id="getKey">
								<Input
									size="sm"
									id="getKey"
									type="text"
									placeholder="e.g. getApplication()_uk_individual"
									value={getKey}
									onChange={(evt: ChangeEvent<HTMLInputElement>) => {
										setGetKey(evt.target.value);
									}}
								/>
							</AccessibleFormControl>
							<Flex gap={2}>
								<Button size="sm" onClick={getWithState} isDisabled={!getKey}>
									Get...
								</Button>
							</Flex>
						</Flex>
						<Flex flexDirection="column" gap={4}>
							<Heading fontSize={18}>Delete</Heading>
							<AccessibleFormControl id="deleteKey">
								<Input
									size="sm"
									id="deleteKey"
									type="text"
									placeholder="e.g. getApplication()_uk_individual"
									value={deleteKey}
									onChange={(evt: ChangeEvent<HTMLInputElement>) => {
										setDeleteKey(evt.target.value);
									}}
								/>
							</AccessibleFormControl>
							<Flex gap={2}>
								<Button size="sm" onClick={deleteWithState} isDisabled={!deleteKey}>
									Delete...
								</Button>
							</Flex>
						</Flex>
						<Flex flexDirection="column" gap={4}>
							<Heading fontSize={18}>Revalidate</Heading>
							<AccessibleFormControl id="revalidateKey">
								<Input
									size="sm"
									id="revalidateKey"
									type="text"
									placeholder="e.g. getApplication()_uk_individual"
									value={revalidateKey}
									onChange={(evt: ChangeEvent<HTMLInputElement>) => {
										setRevalidateKey(evt.target.value);
									}}
								/>
							</AccessibleFormControl>
							<Flex gap={2}>
								<Button size="sm" onClick={revalidateWithState} isDisabled={!revalidateKey}>
									Revalidate...
								</Button>
							</Flex>
						</Flex>
						<Flex flexDirection="column" gap={4}>
							<Heading fontSize={18}>Event Tree</Heading>
							<AccessibleFormControl id="eventName">
								<AccessibleSelect
									size="sm"
									id="eventName"
									label="Event Name"
									placeholder="e.g. theme or application etc"
									value={eventName}
									onChange={(evt: ChangeEvent<HTMLSelectElement>) => {
										setEventName(evt.target.value);
									}}
								>
									{getAllEvents().map((eventName) => {
										return (
											<option key={eventName} value={eventName}>
												{eventName}
											</option>
										);
									})}
								</AccessibleSelect>
							</AccessibleFormControl>
							<Flex gap={2}>
								<Button size="sm" onClick={eventTreeWithState} isDisabled={!eventName}>
									Generate...
								</Button>
							</Flex>
						</Flex>
					</Flex>
					<Flex flexDirection="column" flex={2} gap={4} maxWidth="80%">
						<Heading fontSize={18}>Result</Heading>
						<Flex>
							{tabs.length > 0 && (
								<Flex flexWrap="wrap">
									{tabs.map((tab, tabIndex) => {
										return (
											<Flex
												key={tabIndex}
												px={2}
												cursor="pointer"
												onClick={() => {
													return setCurrentTab(tabIndex);
												}}
												fontSize={12}
												alignItems="center"
												bgColor={tabIndex === currentTab ? "gray.200" : ""}
												_hover={{ bgColor: "gray.100" }}
												whiteSpace="wrap"
												wordBreak="break-all"
												maxWidth="600px"
											>
												{tab.isLoading && <Button size="xs" isLoading={true} variant="ghost" mr={1} />}
												{tab.label} <CloseButton onClick={deleteTabHandler(tabIndex)} />
											</Flex>
										);
									})}
								</Flex>
							)}
						</Flex>

						{tabs[currentTab] && tabs[currentTab].type === "scan" && (
							<Flex fontSize={14} flexDirection="column" gap={2}>
								{tabs[currentTab].content.map((scanKey, index) => {
									return (
										<Flex key={index} _hover={{ bgColor: "gray.100" }} whiteSpace="wrap" wordBreak="break-all">
											<Button variant="ghost" size="xs" onClick={getHandler(scanKey)} minWidth="30px">
												GET
											</Button>
											<Button
												variant="ghost"
												size="xs"
												onClick={deleteHandler(scanKey)}
												colorScheme="red"
												minWidth="40px"
											>
												DELETE
											</Button>
											<Button
												variant="ghost"
												size="xs"
												onClick={revalidateHandler(scanKey)}
												colorScheme="blue"
												mr={2}
												minWidth="70px"
											>
												REVALIDATE
											</Button>{" "}
											{scanKey}
										</Flex>
									);
								})}
							</Flex>
						)}

						{tabs[currentTab] && (
							<Editor
								height="600px"
								defaultLanguage="json"
								value={JSON.stringify((tabs[currentTab] || {}).content || "", null, 2)}
								theme="vs-dark"
								options={{
									readOnly: false,
									minimap: { enabled: false }
								}}
							/>
						)}
						{tabs.length === 0 && <Flex>No tabs to display</Flex>}
					</Flex>
				</Flex>

				<Heading fontSize={18}>Stats</Heading>
				<Table flexWrap="wrap" flexDirection="column" gap={1} width="unset">
					<Thead>
						<Tr>
							<Th>Name</Th>
							<Th>Enabled</Th>
							<Th>Requests</Th>
							<Th>Hit</Th>
							<Th>Miss</Th>
							<Th>%</Th>
						</Tr>
					</Thead>
					<Tbody>
						{Object.entries(config).map(([functionName, isEnabled]) => {
							return (
								<Tr key={functionName}>
									<Td>
										<Button variant="ghost" size="xs" onClick={scanHandler(functionName + "*")}>
											SCAN
										</Button>
										{functionName}
									</Td>
									<Td>
										<Badge colorScheme={isEnabled ? "green" : "red"}>
											<span>{isEnabled ? "true" : "false"}</span>
										</Badge>
										&nbsp;
										<Button
											size="xs"
											borderRadius={0}
											variant="ghost"
											onClick={updateConfig(functionName, isEnabled ? "false" : "true")}
										>
											Set&nbsp;
											<span>{isEnabled ? "False" : "True"}</span>
										</Button>
									</Td>
									<Td>{cacheStats[functionName + ":request"] || "0"}</Td>
									<Td>{cacheStats[functionName + ":hit"] || "0"}</Td>
									<Td>{cacheStats[functionName + ":miss"] || "0"}</Td>
									<Td>
										{(cacheStats[functionName + ":request"] || 0) > 0
											? (
													(100 / (cacheStats[functionName + ":request"] || 0)) *
													(cacheStats[functionName + ":hit"] || 0)
												).toFixed(0) + "%"
											: "N/A"}
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>

				{/*{JSON.stringify(cacheStats, null, 2)}*/}
			</Flex>
		</ChakraThemeProvider>
	);
}
